/* eslint-disable max-len */
import React from 'react';
import { Col, Divider, Row } from 'antd';
import { Table, TweetMedia } from '..';
import { PrintDateShort, TimeFormatter } from '../../utils/timeParser';
import { socmedTypeIcon } from '../../configs/socmedType';
import AccountCard from '../accountCard';
import {
  getFormatCurrency,
  getUsernameSocialAccount,
} from '../../utils/commonUtils';
import Wrapper from './style';
import { INTEGRATOR_TWITTER } from '../../configs/integrator';

const {
  BID_STRATEGY_OPTION,
  FREQUENCY_CAP_OPTION,
  AUDIENCE_PLACEMENT_OPTION,
  TARGETING_TYPE,
} = INTEGRATOR_TWITTER();

const onGetDate = (date) => {
  if (!date) return false;
  return PrintDateShort(date);
};

const Description = (props) => {
  const {
    label,
    value,
    emptyText = '(empty)',
    capitalize,
    onParseValue,
  } = props;

  const displayValue = () => {
    if (onParseValue && value) {
      const text = value.replace(/_/g, ' ');
      const parse = text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
      return parse;
    }
    return value;
  };

  return (
    <Row gutter={[4, 4]}>
      {label && (
        <Col span={24} className="font-paragraph_medium black3">
          {label}
        </Col>
      )}
      <Col
        span={24}
        className={`font-paragraph ${value ? 'black1' : 'black4'} ${
          capitalize && 'description_value'
        }`}
      >
        {displayValue() || emptyText}
      </Col>
    </Row>
  );
};

const AdGroup = (props) => {
  const { dataSource } = props;
  const biddingRules = dataSource?.helper?.bidding_rules || {};
  const socmedData = dataSource?.chooseAccount || {};
  const adGroupData = dataSource?.data?.twitter_line_item || {};
  const targetingCriteria = dataSource?.data?.twitter_targeting_criteria;
  const startDate = onGetDate(adGroupData?.start_time);
  const endDate = onGetDate(adGroupData?.end_time);

  const onGetPlatform = targetingCriteria?.filter((item) => {
    return item?.targeting_type === TARGETING_TYPE.PLATFORM;
  });

  const onGetPlatformVersion = targetingCriteria?.filter((item) => {
    return item?.targeting_type === TARGETING_TYPE.PLATFORM_VERSION;
  });

  const onGetLocation = () => {
    const locationData = targetingCriteria?.filter((item) => {
      return item?.targeting_type === TARGETING_TYPE.LOCATION;
    });
    const parseData = [];
    if (locationData?.length !== 0) {
      locationData?.map((item) => {
        let label = '';
        const { name, location_type: type } = item;
        if (type === 'COUNTRIES') label = `Country \u2014 ${name}`;
        if (type === 'REGIONS') label = `Region or state \u2014 ${name}`;
        if (type === 'METROS') label = `Metro \u2014 ${name}`;
        if (type === 'CITIES') label = `City \u2014 ${name}`;
        if (type === 'POSTAL_CODES') {
          const fullName = name;
          const parse = fullName.split(', ');
          label = `Zip code \u2014 ${parse[0]}, ${parse[parse?.length - 1]}`;
        }
        return parseData.push(label);
      });
      return parseData;
    }
    return [];
  };

  const onGetLanguage = targetingCriteria?.filter((item) => {
    return item?.targeting_type === TARGETING_TYPE.LANGUAGE;
  });

  const onGetBidStrategy = () => {
    const selected = BID_STRATEGY_OPTION.find((item) => {
      return item?.value === adGroupData?.bid_strategy;
    });
    if (adGroupData?.automatically_select_bid) return selected;
    const cost = getFormatCurrency({
      currency: biddingRules?.currency,
      value:
        adGroupData?.bid_amount_local_micro /
        biddingRules?.minimum_denomination,
    });
    return {
      label: selected?.label,
      description: cost,
    };
  };

  const onGetFrequencyCap = () => {
    const frequencyCap = adGroupData?.frequency_cap;
    const isAutoCap = frequencyCap === null || frequencyCap === undefined;
    const selected = FREQUENCY_CAP_OPTION.find((item) => {
      return item?.value === isAutoCap;
    });
    if (isAutoCap) {
      return selected?.label;
    }
    return `${frequencyCap} impressions per ${adGroupData?.duration_in_days} days`;
  };

  const onGetAge = () => {
    const ageData = targetingCriteria?.find((item) => {
      return item?.targeting_type === TARGETING_TYPE.AGE;
    });
    if (ageData) {
      const value = ageData?.targeting_value;
      const isOverString = 'AGE_OVER';
      const isOverValue = ageData?.targeting_value?.includes(isOverString);
      if (isOverValue) {
        return `${value?.slice(value?.length - 2)}+`;
      }
      const [start, end] = value?.slice(4).split('_TO_') ?? [
        undefined,
        undefined,
      ];
      return `${start} \u2014 ${end}`;
    }
    return false;
  };

  const onGetPlacements = AUDIENCE_PLACEMENT_OPTION.filter((item) => {
    return adGroupData?.placements?.some((items) => {
      return items === item?.value;
    });
  });

  const onGetGender = targetingCriteria?.find((item) => {
    return item?.targeting_type === TARGETING_TYPE.GENDER;
  });

  const onGetDevice = targetingCriteria?.filter((item) => {
    return item?.targeting_type === TARGETING_TYPE.DEVICE;
  });

  const onGetCarrier = targetingCriteria?.filter((item) => {
    return item?.targeting_type === TARGETING_TYPE.NETWORK_OPERATOR;
  });

  const onGetInterest = () => {
    const interestData = targetingCriteria?.filter((item) => {
      return item?.targeting_type === TARGETING_TYPE.INTEREST;
    });
    const parseData = [];
    if (interestData?.length !== 0) {
      interestData?.map((item) => {
        const interestName = item?.name?.split('/');
        const label = `${interestName[0]} \u2014 ${interestName[1]}`;
        return parseData.push(label);
      });
    }
    return parseData;
  };

  const onGetTvShows = () => {
    const interestData = targetingCriteria?.filter((item) => {
      return item?.targeting_type === TARGETING_TYPE.TV_SHOW;
    });
    const parseData = [];
    if (interestData?.length !== 0) {
      interestData?.map((item) => {
        const genre = item?.genre?.replace(/_/g, ' ');
        const genreParse =
          genre?.charAt(0).toUpperCase() + genre.slice(1).toLowerCase();
        const label = `${genreParse} \u2014 ${item?.name || ''}`;
        return parseData.push(label);
      });
    }
    return parseData;
  };

  const onGetEvents = () => {
    const interestData = targetingCriteria?.filter((item) => {
      return item?.targeting_type === TARGETING_TYPE.EVENT;
    });
    const parseData = [];
    if (interestData?.length !== 0) {
      interestData?.map((item) => {
        const startTime = item?.start_time;
        const timeParse = startTime ? PrintDateShort(startTime) : '-';
        const eventType = item?.event_type;
        const eventParse =
          eventType?.charAt(0)?.toUpperCase() + eventType?.slice(1);
        const label = `${eventParse} \u2014 ${item?.name} \u0040 ${timeParse}`;
        return parseData.push(label);
      });
    }
    return parseData;
  };

  const onGetConversation = targetingCriteria?.filter((item) => {
    return item?.targeting_type === TARGETING_TYPE.CONVERSATION;
  });

  const columns = [
    {
      title: 'Tweet(s)',
      width: 600,
      render: (record) => {
        const { created_at: createdAt, user, full_text: fullText } = record;
        const date = TimeFormatter(createdAt);
        const print = `${date.monthName} ${date.date}`;
        const customName = (
          <div className="component_accountCard_custom_name">
            {socmedTypeIcon.TWT}
            <div className="font-paragraph black1">{user?.name}</div>
            <div className="font-caption black1">{`\u0040${user?.screen_name}`}</div>
            &#8729;
            <div className="font-caption black1">{print}</div>
          </div>
        );
        return (
          <AccountCard
            description={fullText}
            srcAvatar={user?.profile_image_url_https}
            name={customName}
          />
        );
      },
    },
    {
      render: (record) => {
        return <TweetMedia record={record} />;
      },
    },
  ];

  return (
    <Wrapper>
      <Row gutter={[24, 24]}>
        <Col span={24}>
          <Description label="Ad Group Name" value={adGroupData?.name} />
        </Col>
        <Col span={24}>
          <Divider style={{ margin: 0 }} />
        </Col>
        <Col span={24}>
          <Row gutter={[24, 24]}>
            <Col span={24} className="font-subtitle2_medium black1">
              Delivery
            </Col>
            <Col span={24}>
              <Description
                label="Total Budget (Optional)"
                value={getFormatCurrency({
                  currency: biddingRules?.currency,
                  value:
                    adGroupData?.total_budget_amount_local_micro /
                    biddingRules?.minimum_denomination,
                })}
              />
            </Col>
            <Col span={24}>
              <Row gutter={16}>
                <Col span={12}>
                  <Description label="Start Date" value={startDate} />
                </Col>
                <Col span={12}>
                  <Description label="End Date" value={endDate} />
                </Col>
              </Row>
            </Col>
            <Col span={24}>
              <Description
                label="Goal"
                value={adGroupData?.goal}
                onParseValue
              />
            </Col>
            <Col span={24}>
              <Row gutter={[24, 24]}>
                <Col
                  span={24}
                  className="font-paragraph_medium black3 description_label"
                >
                  Bid Strategy
                </Col>
                <Col span={24}>
                  <Row gutter={[4, 4]}>
                    <Col span={24} className="font-paragraph black1">
                      {onGetBidStrategy()?.label}
                    </Col>
                    <Col span={24} className="font-paragraph black3">
                      {onGetBidStrategy()?.description}
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Col>
            <Col span={24}>
              <Description
                label="Pay By"
                value={adGroupData?.pay_by}
                onParseValue
              />
            </Col>
            <Col span={24}>
              <Description label="Frequency cap" value={onGetFrequencyCap()} />
            </Col>
            <Col span={24}>
              <Description
                label="Audience measurement tag (optional)"
                emptyText="There are no partners to select."
              />
            </Col>
            <Col span={24}>
              <Description
                label="Google Campaign Manager impression tag (optional) "
                value={(() => {
                  const trackingTags = adGroupData?.tracking_tags;
                  if (!trackingTags || trackingTags?.length === 0) return null;
                  const onParse = trackingTags?.split('-');
                  return onParse.pop();
                })()}
                emptyText="-"
              />
            </Col>
          </Row>
        </Col>
        <Col span={24}>
          <Divider style={{ margin: 0 }} />
        </Col>
        <Col span={24}>
          <Row gutter={[24, 24]}>
            <Col
              span={24}
              className="font-paragraph_medium black3 description_label"
            >
              Demographics
            </Col>
            <Col span={24}>
              <Row gutter={24} justify="space-between">
                <Col span={10}>
                  <Description
                    label="Age"
                    value={onGetAge()}
                    capitalize="true"
                  />
                </Col>
                <Col span={10}>
                  <Description
                    label="Gender"
                    value={onGetGender?.targeting_value}
                    emptyText="Any"
                    onParseValue
                  />
                </Col>
              </Row>
            </Col>
            <Col span={24}>
              <Row gutter={[4, 4]}>
                <Col span={24} className="font-paragraph_medium black3">
                  Location
                </Col>
                <Col span={24} className="font-paragraph black2">
                  {onGetLocation().length > 1
                    ? onGetLocation()?.map((item) => {
                        return <li key={item}>{item}</li>;
                      })
                    : onGetLocation()?.map((item) => {
                        return <span key={item}>{item}</span>;
                      })}
                  {onGetLocation()?.length === 0 && '-'}
                </Col>
              </Row>
            </Col>
            <Col span={24}>
              <Row gutter={[4, 4]}>
                <Col span={24} className="font-paragraph_medium black3">
                  Languages
                </Col>
                <Col span={24} className="font-paragraph black2">
                  {onGetLanguage?.length > 1
                    ? onGetLanguage?.map((item) => {
                        return (
                          <li key={item?.targeting_value}>{item?.name}</li>
                        );
                      })
                    : onGetLanguage?.map((item) => {
                        return (
                          <span key={item?.targeting_value}>{item?.name}</span>
                        );
                      })}
                  {onGetLanguage?.length === 0 && '-'}
                </Col>
              </Row>
            </Col>
          </Row>
        </Col>
        <Col span={24}>
          <Divider style={{ margin: 0 }} />
        </Col>
        <Col span={24}>
          <Row gutter={[24, 24]}>
            <Col span={24} className="font-subtitle2_medium black1">
              Devices
            </Col>
            <Col span={24}>
              <Row gutter={[4, 4]}>
                <Col span={24} className="font-paragraph_medium black3">
                  Operating System
                </Col>
                <Col span={24}>
                  {onGetPlatform?.length > 0 &&
                    onGetPlatform?.map((item) => {
                      const platformVersion = onGetPlatformVersion?.find(
                        (platform) => {
                          return platform?.os_type === item?.name;
                        }
                      );
                      const osType = platformVersion?.os_type;
                      const osVersion = platformVersion?.number;
                      const label = platformVersion
                        ? `${osType} \u2014 version ${osVersion} and Above`
                        : `${item?.name} \u2014 All`;
                      return (
                        <li key={label} className="font-paragraph black2">
                          {label}
                        </li>
                      );
                    })}
                  {onGetPlatform?.length === 0 && '-'}
                </Col>
              </Row>
            </Col>
            <Col span={24}>
              <Row gutter={[4, 4]}>
                <Col span={24} className="font-paragraph_medium black3">
                  Device model (optional)
                </Col>
                <Col span={24} className="font-paragraph black2">
                  {onGetDevice?.length > 1
                    ? onGetDevice?.map((item) => {
                        return (
                          <li key={item?.targeting_value}>{item?.name}</li>
                        );
                      })
                    : onGetDevice?.map((item) => {
                        return (
                          <span key={item?.targeting_value}>{item?.name}</span>
                        );
                      })}
                  {onGetDevice?.length === 0 && '-'}
                </Col>
              </Row>
            </Col>
            <Col span={24}>
              <Row gutter={[4, 4]}>
                <Col span={24} className="font-paragraph_medium black3">
                  Carrier (optional)
                </Col>
                <Col span={24} className="font-paragraph black2">
                  {onGetCarrier?.length > 1
                    ? onGetCarrier?.map((item) => {
                        return (
                          <li key={item?.targeting_value}>{item?.name}</li>
                        );
                      })
                    : onGetCarrier?.map((item) => {
                        return (
                          <span key={item?.targeting_value}>{item?.name}</span>
                        );
                      })}
                  {onGetCarrier?.length === 0 && '-'}
                </Col>
              </Row>
            </Col>
            <Col span={24}>
              <Description
                label="Target people who first used Twitter on a new device or carrier"
                value={
                  dataSource?.target_people
                    ? `${dataSource?.target_people?.label} - ${dataSource?.target_people_month} month`
                    : false
                }
              />
            </Col>
          </Row>
        </Col>
        <Col span={24}>
          <Divider style={{ margin: 0 }} />
        </Col>
        <Col span={24}>
          <Row gutter={[24, 24]}>
            <Col span={24} className="font-subtitle2_medium black1">
              Targeting features
            </Col>
            <Col span={24}>
              <Row gutter={[4, 4]}>
                <Col span={24} className="font-paragraph_medium black3">
                  Interests (optional)
                </Col>
                <Col span={24} className="font-paragraph black2">
                  {onGetInterest()?.length > 1
                    ? onGetInterest()?.map((item) => {
                        return <li key={item}>{item}</li>;
                      })
                    : onGetInterest()?.map((item) => {
                        return <span key={item}>{item}</span>;
                      })}
                  {onGetInterest()?.length === 0 && '-'}
                </Col>
              </Row>
            </Col>
            <Col span={24}>
              <Row gutter={[4, 4]}>
                <Col span={24} className="font-paragraph_medium black3">
                  Movies and TV shows (optional)
                </Col>
                <Col span={24} className="font-paragraph black2">
                  {onGetTvShows()?.length > 1
                    ? onGetTvShows()?.map((item) => {
                        return <li key={item}>{item}</li>;
                      })
                    : onGetTvShows()?.map((item) => {
                        return <span key={item}>{item}</span>;
                      })}
                  {onGetTvShows()?.length === 0 && '-'}
                </Col>
              </Row>
            </Col>
            <Col span={24}>
              <Row gutter={[4, 4]}>
                <Col span={24} className="font-paragraph_medium black3">
                  Events (optional)
                </Col>
                <Col span={24} className="font-paragraph black2">
                  {onGetEvents()?.length > 1
                    ? onGetEvents()?.map((item) => {
                        return <li key={item}>{item}</li>;
                      })
                    : onGetEvents()?.map((item) => {
                        return <span key={item}>{item}</span>;
                      })}
                  {onGetEvents()?.length === 0 && '-'}
                </Col>
              </Row>
            </Col>
            <Col span={24}>
              <Row gutter={[4, 4]}>
                <Col span={24} className="font-paragraph_medium black3">
                  Conversation topics (optional)
                </Col>
                <Col span={24} className="font-paragraph black2">
                  {onGetConversation?.length > 1
                    ? onGetConversation?.map((item) => {
                        return (
                          <li key={item?.targeting_value}>{item?.name}</li>
                        );
                      })
                    : onGetConversation?.map((item) => {
                        return (
                          <span key={item?.targeting_value}>{item?.name}</span>
                        );
                      })}
                  {onGetConversation?.length === 0 && '-'}
                </Col>
              </Row>
            </Col>
          </Row>
        </Col>
        <Col span={24}>
          <Divider style={{ margin: 0 }} />
        </Col>
        <Col span={24}>
          <Row gutter={[24, 24]}>
            <Col span={24}>
              <Description
                label="Expand your audience (optional)"
                value={
                  adGroupData?.audience_expansion &&
                  `Yes - ${adGroupData?.audience_expansion}`
                }
                emptyText="None"
                onParseValue
              />
            </Col>
          </Row>
        </Col>
        <Col span={24}>
          <Divider style={{ margin: 0 }} />
        </Col>
        <Col span={24}>
          <Row gutter={[24, 24]}>
            <Col span={24}>
              <Description
                label={`Also target followers of \u0040${getUsernameSocialAccount(
                  socmedData?.url
                )} (optional)`}
                value={adGroupData?.also_target && 'Active'}
                emptyText="Inactive"
              />
            </Col>
          </Row>
        </Col>
        <Col span={24}>
          <Divider style={{ margin: 0 }} />
        </Col>
        <Col span={24}>
          <Row gutter={[24, 24]}>
            <Col span={24} className="font-subtitle2_medium black1">
              Placements
            </Col>
            <Col span={24}>
              <Row gutter={[12, 12]}>
                <Col span={24} className="font-paragraph_medium black3">
                  Twitter placements
                </Col>
                <Col span={24} className="font-caption black2">
                  Put your ads where your audience is most likely to see them.
                </Col>
                <Col span={24} className="font-paragraph black2">
                  {onGetPlacements?.map((item) => {
                    return <li key={item?.value}>{item?.label}</li>;
                  })}
                </Col>
              </Row>
            </Col>
          </Row>
        </Col>
        <Col span={24}>
          <Divider style={{ marginBottom: 0 }} />
        </Col>
        <Col span={24}>
          <Row gutter={[24, 24]}>
            <Col span={24} className="font-subtitle2_medium black1">
              Creatives
            </Col>
            {/* <Col span={24}>
              <Row gutter={[12, 12]}>
                <Col span={24} className="font-paragraph_medium black3">
                  Campaign Code
                </Col>
                <Col span={24} className="font-paragraph black2">
                  Automatically generated by alphaveritas and added into your
                  ads media
                </Col>
              </Row>
            </Col>
            <Col span={24}>
              <Col className="component_ad_group_creatives_content_media">
                <Image src="https://images.pexels.com/photos/1092671/pexels-photo-1092671.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" />
              </Col>
            </Col> */}
            <Col span={24}>
              <Table
                style={{ margin: 0 }}
                columns={columns}
                dataSource={adGroupData?.tweets}
                pagination={false}
                rowKey="id"
                borderLess
              />
            </Col>
          </Row>
        </Col>
      </Row>
    </Wrapper>
  );
};

export default AdGroup;
